import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { authorizedFetch, json } from '@nimbitlabs/ui';
import { useAccountStore } from '../account/accountStore';

export interface AuditFilters {
  startDate?: string;
  endDate?: string;
  userId?: string;
  action?: string;
}

export interface AuditLog {
  id: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string;
  changes: any;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
}

interface AuditState {
  logs: AuditLog[];
  filters: AuditFilters;
  loading: boolean;
  error: string | null;
  
  fetchAuditLogs: (filters?: AuditFilters) => Promise<void>;
  setFilters: (filters: AuditFilters) => void;
  clearError: () => void;
  reset: () => void;
}

export const useAuditStore = create<AuditState>()(
  subscribeWithSelector((set, get) => ({
  logs: [],
  filters: {},
  loading: false,
  error: null,
  
  fetchAuditLogs: async (filters?: AuditFilters) => {
    const activeFilters = filters || get().filters;
    set({ loading: true, error: null, filters: activeFilters });
    
    try {
      const params = new URLSearchParams();
      if (activeFilters.startDate) params.append('startDate', activeFilters.startDate);
      if (activeFilters.endDate) params.append('endDate', activeFilters.endDate);
      if (activeFilters.userId) params.append('userId', activeFilters.userId);
      if (activeFilters.action) params.append('action', activeFilters.action);
      
      const queryString = params.toString();
      const url = `/audit${queryString ? `?${queryString}` : ''}`;
      
      const response = await authorizedFetch(url);
      const logs = await json<AuditLog[]>(response);
      set({ logs, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch audit logs', loading: false });
    }
  },
  
  setFilters: (filters: AuditFilters) => {
    set({ filters });
    get().fetchAuditLogs(filters);
  },
  
  clearError: () => set({ error: null }),
  
  reset: () => set({ logs: [], filters: {}, loading: false, error: null }),
})));

// Subscribe to account changes and auto-fetch data
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      useAuditStore.getState().fetchAuditLogs();
    } else {
      useAuditStore.getState().reset();
    }
  }
);
